import { match } from 'ts-pattern';
import { z } from 'zod';

import {
  EXAMPLE_TYPES, ExampleTypeEnum, METRIC_TYPES, MetricTypeEnum
} from '@/types/evals';

// API Classifier config (form shape). Headers are edited as rows and pruned on
// submit; isAsync is a simple toggle.
export const apiConfigSchema = z.object( {
  url: z.string().min( 1, 'API URL is required' ),
  headers: z.array( z.object( {
    header: z.string(),
    value: z.string()
  } ) ).optional(),
  isAsync: z.boolean().optional()
} );

// LLM-as-a-Judge config (form shape). Temperature is a number in the form
// (-0.1 is the "None"/model-default sentinel) and is serialized to the domain
// string on submit. Prompt parameters are edited as key/value rows; if present,
// each row must be complete.
export const llmConfigSchema = z.object( {
  model: z.string().min( 1, 'Model is required' ),
  prompt: z.string().min( 1, 'Grading prompt is required' ),
  temperature: z.number().optional(),
  promptParameters: z.array( z.object( {
    key: z.string().min( 1, 'Parameter key is required' ),
    value: z.string().min( 1, 'Parameter value is required' )
  } ) ).optional()
} );

// Full metric form schema. `config` is validated dynamically against the
// selected metric type via superRefine (mirrors the source create modal).
export const metricFormSchema = z.object( {
  name: z.string().min( 1, 'Name is required' ),
  description: z.string().optional(),
  metric_type: z.enum( [ METRIC_TYPES.API_CLASSIFIER, METRIC_TYPES.LLM_AS_A_JUDGE ], { message: 'Please select a metric type' } ),
  exampleType: z.array( z.enum( [ EXAMPLE_TYPES.CONVERSATION, EXAMPLE_TYPES.MESSAGE ] ) ).min( 1, 'Select at least one example type' ),
  // Comma-separated in the form; split + trimmed on submit. At least one
  // non-empty value is required.
  producesMetrics: z.string().refine(
    ( s ) => s.split( ',' ).some( ( p ) => p.trim().length > 0 ),
    'At least one produced metric is required'
  ),
  config: z.any()
} ).superRefine( ( data, ctx ) => {
  match( data.metric_type )
    .with( METRIC_TYPES.API_CLASSIFIER, () => {
      const result = apiConfigSchema.safeParse( data.config );
      if ( !result.success ) {
        result.error.issues.forEach( ( issue ) => ctx.addIssue( { ...issue, path: [ 'config', ...issue.path ] } ) );
      }
    } )
    .with( METRIC_TYPES.LLM_AS_A_JUDGE, () => {
      const result = llmConfigSchema.safeParse( data.config );
      if ( !result.success ) {
        result.error.issues.forEach( ( issue ) => ctx.addIssue( { ...issue, path: [ 'config', ...issue.path ] } ) );
      }
    } )
    .otherwise( () => {} );
} );

// Form-input shape for the metric config. `config` on the form schema is
// `z.any()` (the schema validates it by metric type via superRefine), so this
// is the concrete shape consumers read/write through react-hook-form.
export type MetricConfigFormData = {
  // API Classifier
  url?: string;
  headers?: { header: string; value: string }[];
  isAsync?: boolean;
  // LLM as a Judge
  model?: string;
  prompt?: string;
  temperature?: number;
  promptParameters?: { key: string; value: string }[];
};

export type MetricFormData = {
  name: string;
  description?: string;
  metric_type: MetricTypeEnum;
  exampleType: ExampleTypeEnum[];
  producesMetrics: string;
  config: MetricConfigFormData;
};
